import type {
	BrandCollection,
	CoreApiError,
	CoreApiPaginatedMetaResponse,
	PaginatedParams,
	PaginatedQueryOptions,
} from '@lqa-frontend/api-services';
import type { UndefinedInitialDataInfiniteOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
	usePaginatedQuery,
} from '@lqa-frontend/api-services';
import { useInfiniteQuery } from '@tanstack/react-query';

export function useBrandsQuery(
	params: BrandsQueryParams,
	options?: PaginatedQueryOptions<BrandCollection>
) {
	return usePaginatedQuery(endpoints.brands.index, params, options);
}

export interface BrandsQueryParams extends PaginatedParams {
	name?: string;
	brandIds?: number[];
	clientIds?: number[];
	withCustomPricing?: boolean;
}

export function useBrandsInfiniteQuery(
	params: BrandsQueryParams,
	options?: BrandsInfiniteQueryOptions
) {
	return useInfiniteQuery<
		CoreApiPaginatedMetaResponse<BrandCollection>,
		AxiosError<CoreApiError>
	>({
		queryKey: coreApiQueryKey(endpoints.myLQA.pricing.brands, 'get', params),
		queryFn: async ({ pageParam = 1 }) =>
			coreApiClient
				.get<CoreApiPaginatedMetaResponse<BrandCollection>>(
					endpoints.myLQA.pricing.brands,
					{
						params: {
							...params,
							page: pageParam,
						},
					}
				)
				.then((response) => response.data),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.meta.currentPage < lastPage.meta.totalPages
				? lastPage.meta.currentPage + 1
				: null,
		...options,
	});
}

export type BrandsInfiniteQueryOptions = Omit<
	UndefinedInitialDataInfiniteOptions<
		CoreApiPaginatedMetaResponse<BrandCollection>,
		AxiosError<CoreApiError>
	>,
	'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>;
